import { Directive, ElementRef, Input, OnChanges } from '@angular/core';
import { normalizeLogoUrl } from '../pipes/logo-url.pipe';

/**
 * Sets a thumbnail's background-image to a logo, falling back to a local default.
 *
 * A plain [style.background-image] cannot fall back, because Google's favicon
 * service answers "no favicon for this domain" with a 16x16 grey globe and a 404
 * status - and a browser paints that body regardless of the status code. Stretched
 * over a 50px circle by background-size:cover it reads as an empty smudge, which
 * is what "logo not displaying" actually looks like. So the image is loaded first
 * and only used when it comes back at a usable size; anything under 32px is either
 * the globe or a favicon too small to survive the upscale.
 *
 * The element keeps its own class and every style that goes with it - only the
 * background-image value is decided here.
 */
@Directive({ selector: '[logoBackground]' })
export class LogoBackgroundDirective implements OnChanges {
	@Input('logoBackground') logoUrl: string | null | undefined;
	@Input() logoFallback: string = 'assets/images/education-default.jpg';

	private static readonly MIN_USABLE_PX = 32;

	constructor(private hostRef: ElementRef<HTMLElement>) { }

	ngOnChanges(): void {
		this.apply(this.logoFallback);

		const url = normalizeLogoUrl(this.logoUrl);
		if (!url) return;

		const probe = new Image();
		probe.onload = () => {
			if (probe.naturalWidth >= LogoBackgroundDirective.MIN_USABLE_PX) {
				this.apply(url);
			}
		};
		probe.src = url;
	}

	private apply(url: string) {
		this.hostRef.nativeElement.style.backgroundImage = `url('${url}')`;
	}
}
